import {
  Input,
  Table,
  Typography,
  Button,
  Drawer,
  Form,
  InputNumber,
  Space,
  message,
  Descriptions,
  Image,
  Select,
  Tag,
} from "antd";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature, IMAGE_URL } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { useQuery } from "@tanstack/react-query";
import {
  FetchRemittance_api,
  UpdateRemittance,
  FetchBankAccounts_api,
} from "../../Api";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { Pencil, Eye, Landmark } from "lucide-react";
import { useAuthChecker } from "../../Context";

const { Text } = Typography;
const { Option } = Select;

export const RemittancePage = () => {
  const { isLoading, hasPermission } = usePagePermission(
    AppFeature.REMITTANCE,
    ["admin", "cashier", "manager"],
  );

  const [remittanceData, setRemittanceData] = useState<any>([]);
  const [searchKeywordForCustomerSearch, setSearchKeywordForCustomerSearch] =
    useState("");
  const [debouncedQueryForRemittanceSearch] = useDebounce(
    searchKeywordForCustomerSearch,
    500,
  );

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedRemittance, setSelectedRemittance] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [form] = Form.useForm();

  const { user, isAdmin } = useAuthChecker();

  const { refetch: FetchRemittanceData, isFetching: isFetchingRemittance } =
    useQuery<any>({
      queryKey: [
        "searchQueryRemittanqce",
        debouncedQueryForRemittanceSearch,
        page,
        pageSize,
      ],
      queryFn: async () => {
        const response: any = await FetchRemittance_api(
          debouncedQueryForRemittanceSearch.trim(),
          page,
          pageSize,
        );
        setRemittanceData(response.remittanceData);
        setTotal(response.total);
        return response;
      },
      enabled: true,
      refetchOnWindowFocus: false,
    });

  const { data: bankAccounts, isFetching: isFetchingBanks } = useQuery({
    queryKey: ["bankAccounts"],
    queryFn: async () => {
      const res = await FetchBankAccounts_api();
      return res?.bankAccounts || [];
    },
    refetchOnWindowFocus: false,
  });

  const handleEdit = (record: any) => {
    setSelectedRemittance(record);
    form.setFieldsValue({
      transfer_id: record.transfer_id,
      slip_date: record.slip_date,
      amount: record.amount,
      bank_account_id: record.bank_account_id
        ? parseInt(record.bank_account_id)
        : undefined,
    });
    setEditDrawerOpen(true);
  };

  const handleUpdate = async (values: any) => {
    setIsUpdating(true);
    try {
      const payload = {
        remittanceId: selectedRemittance.remittance_id,
        userId: user?.userId,
        formData: {
          transfer_id: values.transfer_id,
          slip_date: values.slip_date,
          amount: values.amount,
          bank_account_id: values.bank_account_id,
        },
      };

      const res = await UpdateRemittance(payload);
      if (res?.isDataUpdated) {
        message.success(res.msg);
        setEditDrawerOpen(false);
        FetchRemittanceData();
      } else {
        message.error(res.msg || "Failed to update remittance");
      }
    } catch (error) {
      message.error("Error updating remittance");
    } finally {
      setIsUpdating(false);
    }
  };

  const remittanceColumns = [
    {
      title: "ID",
      dataIndex: "remittance_id",
      key: "remittance_id",
      width: 70,
    },
    {
      title: "Bank Account",
      key: "bank_account",
      render: (record: any) =>
        record.bank_account_name ? (
          <div className="flex flex-col">
            <span className="font-semibold text-blue-700 text-xs">
              {record.bank_account_name}
            </span>
            <span className="text-gray-500 text-[10px]">
              {record.bank_name}
            </span>
          </div>
        ) : (
          <Tag color="default">Legacy Record</Tag>
        ),
    },
    {
      title: "Transfer ID",
      dataIndex: "transfer_id",
      key: "transfer_id",
    },
    {
      title: "Slip Date",
      dataIndex: "slip_date",
      key: "slip_date",
    },
    {
      title: "Added Date",
      dataIndex: "added_date_time",
      key: "added_date_time",
      width: 180,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value: any) => (
        <span className="font-semibold text-blue-600">
          Rs:{" "}
          {Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (record: any) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<Eye className="w-4 h-4 text-blue-500" />}
            onClick={() => {
              setSelectedRemittance(record);
              setViewDrawerOpen(true);
            }}
          />
          {isAdmin && (
            <Button
              type="link"
              size="small"
              icon={<Pencil className="w-4 h-4 text-orange-500" />}
              onClick={() => handleEdit(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  const inquiryColumns = [
    {
      title: "Inquiry ID",
      dataIndex: "inquiry_id",
      key: "inquiry_id",
      width: 120,
    },
    {
      title: "Inquiry Number",
      dataIndex: "inquiryNumber",
      key: "inquiryNumber",
    },
    {
      title: "Vehicle Type",
      dataIndex: "vehicleType",
      key: "vehicleType",
    },
    {
      title: "Vehicle Model",
      dataIndex: "vehicleModel",
      key: "vehicleModel",
    },
    {
      title: "Total Amount",
      dataIndex: "totalamount",
      key: "totalamount",
      render: (value: any) => (
        <span className="font-semibold text-blue-600">
          Rs:{" "}
          {Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "isCompanyPaid",
      key: "isCompanyPaid",
      render: (value: any) => (
        <span
          className={`font-semibold ${value != 0 ? "text-green-600" : "text-red-600"}`}
        >
          {value != 0 ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Company Paid Amount",
      dataIndex: "company_paid_amount",
      key: "company_paid_amount",
      render: (value: any) => (
        <span className="font-semibold text-green-600">
          {Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Vehicle ID",
      key: "vehicle_id_details",
      render: (record: any) =>
        record.engine_number ? (
          <div className="flex flex-col text-xs">
            <span className="font-semibold text-gray-700">
              ENG: {record.engine_number}
            </span>
            <span className="text-gray-500">CHAS: {record.chassis_number}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">Not Assigned</span>
        ),
    },
  ];

  if (isLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <>
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="flex justify-between items-center px-6">
          <h1 className="text-2xl font-semibold">Remittance History</h1>
        </div>
        <div className="flex flex-col space-y-6">
          <div className="px-6 pb-6">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <Input
                type="text"
                placeholder="Search by Transfer ID or Inquiry Number..."
                value={searchKeywordForCustomerSearch}
                onChange={(e) => {
                  setSearchKeywordForCustomerSearch(e.target.value);
                  setPage(1); // Reset page on search
                }}
                className="border w-[25vw] border-gray-300 rounded-lg px-3 py-2 mb-4"
              />

              <Table
                dataSource={Object.values(remittanceData || [])}
                loading={isFetchingRemittance}
                rowKey="remittance_id"
                className="overflow-x-auto"
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: total,
                  onChange: (p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                  },
                  showSizeChanger: true,
                }}
                bordered
                columns={remittanceColumns}
                expandable={{
                  expandedRowRender: (record: any) => (
                    <div className="p-1 bg-gray-50 rounded-lg">
                      <Table
                        className="overflow-x-auto"
                        dataSource={record.inquiries}
                        rowKey="inquiry_id"
                        pagination={false}
                        size="small"
                        columns={inquiryColumns}
                      />
                    </div>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Drawer
        title="Edit Remittance"
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        width={450}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Form.Item
            name="bank_account_id"
            label="Received Bank Account"
            rules={[{ required: true, message: "Please select bank account" }]}
          >
            <Select placeholder="Select Account" loading={isFetchingBanks}>
              {bankAccounts?.map((acc: any) => (
                <Option key={acc.id} value={acc.id}>
                  {acc.account_name} ({acc.bank_name})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="transfer_id"
            label="Transfer ID"
            rules={[{ required: true, message: "Please enter Transfer ID" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="slip_date"
            label="Slip Date"
            rules={[{ required: true, message: "Please enter Slip Date" }]}
          >
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount (LKR)"
            rules={[{ required: true, message: "Please enter Amount" }]}
          >
            <InputNumber
              className="w-full"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item className="mt-8">
            <Space className="w-full justify-end">
              <Button onClick={() => setEditDrawerOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isUpdating}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title="Remittance Details"
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        width={500}
      >
        {selectedRemittance && (
          <Space direction="vertical" size="large" className="w-full">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Bank Account">
                {selectedRemittance.bank_account_name ? (
                  <span className="font-semibold text-blue-600">
                    <Landmark className="inline w-4 h-4 mr-1 mb-1" />{" "}
                    {selectedRemittance.bank_account_name} (
                    {selectedRemittance.bank_name})
                  </span>
                ) : (
                  "Not Specified"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Remittance ID">
                {selectedRemittance.remittance_id}
              </Descriptions.Item>
              <Descriptions.Item label="Transfer ID">
                {selectedRemittance.transfer_id}
              </Descriptions.Item>
              <Descriptions.Item label="Slip Date">
                {selectedRemittance.slip_date}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <span className="font-bold text-blue-600">
                  Rs:{" "}
                  {Number(selectedRemittance.amount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Added At">
                {selectedRemittance.added_date_time}
              </Descriptions.Item>
            </Descriptions>

            {selectedRemittance.imageName && (
              <div className="mt-4">
                <Text strong className="block mb-2 text-gray-600 font-medium">
                  Payment Slip Image
                </Text>
                <Image
                  src={`${IMAGE_URL}/upload/${selectedRemittance.imageName}`}
                  alt="Remittance Slip"
                  className="rounded-lg shadow-sm border w-full"
                />
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </>
  );
};
